import React, {
  Dispatch,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import validator from 'validator'

import MultiInputTextField from '../../components/common/MultiInputTextField'
import { isValidInput } from '../../utils'
import TextInput from '../common/TextInput'

import { UsagePermissionsInfo } from './types'

type AddUsagePermissionsFormProps = {
  entityData: UsagePermissionsInfo
  setEntityData: Dispatch<React.SetStateAction<UsagePermissionsInfo>>
  setFormReady: Dispatch<React.SetStateAction<boolean>>
  setDataChanged: Dispatch<React.SetStateAction<boolean>>
}
// create a Ref type that extends UsagePermissionFormProps
// and adds a validate method
export type AddUsagePermissionsFormPropsRef = AddUsagePermissionsFormProps & {
  validate: () => void
}

const AddUsagePermissionsForm = forwardRef(
  (
    {
      entityData,
      setEntityData,
      setFormReady,
      setDataChanged,
    }: AddUsagePermissionsFormProps,
    ref,
  ) => {
    const allowedChars = ' -'
    const [usageErrorMessage, setUsageErrorMessage] = useState('')
    const [entraIdGroupErrorMessage, setEntraIdGroupErrorMessage] = useState('')
    const [emailsErrorMessage, setEmailsErrorMessage] = useState('')
    const [emailsString, setEmailsString] = useState(entityData.emails)
    const [rendered, setRendered] = useState(false)
    const { t } = useTranslation()

    useEffect(() => {
      setFormReady(false)
      setRendered(true)
    }, [])

    useEffect(() => {
      if (rendered) {
        validateForm(emailsString)
      }
      setEntityData({
        ...entityData,
        emails: emailsString,
      })
    }, [emailsString])

    const isFormValid = (currentEmails = ''): boolean => {
      // Clear error Message
      setUsageErrorMessage('')
      setEmailsErrorMessage('')
      setEntraIdGroupErrorMessage('')

      let hasErrors = false

      // Get Props email value if currentEmails is empty
      currentEmails = currentEmails === '' ? emailsString : currentEmails

      if (entityData.usage.trim() !== '') {
        if (!isValidInput(entityData.usage, allowedChars)) {
          // Invalid usage error message for alphanumeric and spaces
          setUsageErrorMessage(t('usage_permission.invaild'))
          hasErrors = true
        }
      } else {
        setUsageErrorMessage(t('usage_permission.required'))
        hasErrors = true
      }

      if (
        currentEmails.trim() === '' &&
        entityData.entraIDGroup.trim() === ''
      ) {
        setEmailsErrorMessage(t('usage_permission.aad_or_email'))
        setEntraIdGroupErrorMessage(t('usage_permission.aad_or_email'))
        hasErrors = true
      } else if (entityData.entraIDGroup.trim() !== '') {
        if (!isValidInput(entityData.entraIDGroup, allowedChars)) {
          // Invalid usage error message for alphanumeric and spaces
          setEntraIdGroupErrorMessage(
            t('usage_permission.invalid_aad_revocation'),
          )
          hasErrors = true
        }
      }

      return !hasErrors
    }

    const updateValidEmails = (emails: string[]): boolean => {
      setEmailsErrorMessage('')
      let isValid = false
      if (emails.length > 0) {
        let invalidEmailFound = false
        for (const email of emails) {
          if (validator.isEmail(email) === false) {
            invalidEmailFound = true
            //remove email that failed validation
            const emailsToUpdate = [...emails]
            const index = emailsToUpdate.indexOf(email)
            emailsToUpdate.splice(index, 1)
            const updatedEmails = [...emailsToUpdate]

            const emailString = updatedEmails.join(',')
            setEmailsString(emailString)

            setEmailsErrorMessage(t('usage_permission.invalid_email'))
            break
          }
        }

        if (!invalidEmailFound) {
          setEmailsString(emails.join(','))
          isValid = true
        }
      } else {
        setEmailsString('')
      }
      return isValid
    }

    const validateForm = (currentEmails: string) => {
      if (isFormValid(currentEmails)) {
        setFormReady(true)
      } else {
        setFormReady(false)
      }
    }

    useImperativeHandle(ref, () => ({
      validate() {
        validateForm('')
      },
      entityData: entityData,
    }))

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target
      setEntityData({ ...entityData, [name]: value })
      setDataChanged(true)
    }

    const handleSelectedInput = (items: string[]) => {
      updateValidEmails(items)
      setDataChanged(true)
    }

    return (
      <>
        <Form.Group controlId="formFile" className="mb-3">
          <TextInput
            name="usage"
            label={t('usage_permission.name')}
            value={entityData.usage}
            onKeyUp={() => validateForm('')}
            onChange={handleChange}
            error={usageErrorMessage}
            maxLength={20}
          />
        </Form.Group>
        <Form.Group controlId="formFile" className="mb-3">
          <TextInput
            name="entraIDGroup"
            label={t('key.aad_revocation')}
            value={entityData.entraIDGroup}
            onKeyUp={() => validateForm('')}
            onChange={handleChange}
            error={entraIdGroupErrorMessage}
          />
        </Form.Group>
        <Form.Group controlId="formFile" className="mb-3">
          <MultiInputTextField
            selectedInput={handleSelectedInput}
            name="emails"
            placeholder={t('usage_permission.enter_email')}
            label={t('usage_permission.email_address')}
            value={emailsString}
            inputs={emailsString.split(',')}
            validateFn={updateValidEmails}
            error={emailsErrorMessage}
            tooltip={t('usage_permission.email_tooltip')}
          />
        </Form.Group>
      </>
    )
  },
)
AddUsagePermissionsForm.displayName = 'AddUsagePermissionsForm'

export default AddUsagePermissionsForm
